import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useToast,
} from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'
import { FC } from 'react'
import { MdContentCopy, MdMoreHoriz, MdSystemUpdateAlt } from 'react-icons/md'

import { useEditorStore } from '../EditorStoreProvider'
import { EditorStoreState } from '../useQuestionnaireEditorStore'
import { parseAndValidateQuestionnaire } from '../utils/questionnaireValidation'

type QuestionnaireActionsMenuProps = {
  showExport?: boolean
}

const select = (state: EditorStoreState) => ({
  importQuestionnaire: state.importQuestionnaire,
  exportQuestionnaire: state.exportQuestionnaire,
})

export const QuestionnaireActionsMenu: FC<QuestionnaireActionsMenuProps> = ({
  showExport = true,
}) => {
  const { importQuestionnaire, exportQuestionnaire } = useEditorStore(select)
  const toast = useToast()

  const handleExport = () => {
    const questionnaire = exportQuestionnaire()
    navigator.clipboard.writeText(JSON.stringify(questionnaire, undefined, 2))
    toast({
      status: 'info',
      title: t`Questionnaire copied to clipboard`,
    })
  }

  const handleImport = async () => {
    try {
      // Get the clipboard text
      const text = await navigator.clipboard.readText()

      // Use our utility to parse, validate and clean the questionnaire
      const validQuestionnaire = parseAndValidateQuestionnaire(text)

      importQuestionnaire(validQuestionnaire)
      toast({
        status: 'success',
        title: t`Questionnaire was successfully imported from clipboard`,
      })
    } catch (error) {
      toast({
        status: 'error',
        title: t`Couldn't import questionnaire`,
        description:
          error instanceof Error
            ? error.message
            : t`Make sure that you have copied a valid questionnaire to the clipboard.`,
      })
    }
  }

  return (
    <Menu>
      <MenuButton
        as={IconButton}
        aria-label={t`Options`}
        fontSize="xl"
        icon={<MdMoreHoriz />}
        variant="ghost"
      />
      <MenuList>
        {showExport && (
          <MenuItem onClick={handleExport} icon={<MdSystemUpdateAlt />}>
            <Trans>Copy questionnaire to clipboard</Trans>
          </MenuItem>
        )}
        <MenuItem onClick={handleImport} icon={<MdContentCopy />}>
          <Trans>Import questionnaire from clipboard</Trans>
        </MenuItem>
      </MenuList>
    </Menu>
  )
}
